import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, ArrowUpRight, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";

function formatDate(iso, lang) {
    try {
        return new Intl.DateTimeFormat(
            lang === "hu" ? "hu-HU" : lang === "en" ? "en-GB" : "de-AT",
            { day: "2-digit", month: "long", year: "numeric" }
        ).format(new Date(iso));
    } catch {
        return iso;
    }
}

const categoryColors = {
    milestone: "bg-caireen-olive/15 text-caireen-olive",
    event: "bg-caireen-orange/15 text-caireen-orange",
    call: "bg-caireen-blue/15 text-caireen-blue",
};

export default function News() {
    const { t, lang } = useLang();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        api.getNews()
            .then((d) => active && setItems(d.items || []))
            .catch(() => active && setItems([]))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, []);

    return (
        <div data-testid="page-news" className="px-6 md:px-10 py-12 md:py-20">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-caireen-yellow/20 text-caireen-ink font-sans text-xs tracking-widest uppercase">
                        <Calendar className="w-3.5 h-3.5" />
                        {t.nav.news}
                    </span>
                    <h1 className="mt-5 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">
                        {t.newsPage.title}
                    </h1>
                    <p className="mt-5 font-sans text-lg text-caireen-ink/70">{t.newsPage.sub}</p>
                </motion.div>

                {loading ? (
                    <div className="mt-16 flex items-center justify-center text-caireen-ink/40">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : (
                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-7">
                        {items.map((n, i) => (
                            <motion.article
                                key={n.id}
                                data-testid={TID.newsCard(n.slug)}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                whileHover={{ y: -4 }}
                                className={`group bg-white rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10 hover:shadow-[0_24px_60px_-24px_rgba(31,41,55,0.18)] transition-all flex flex-col ${
                                    i === 0 ? "lg:col-span-7 lg:row-span-2" : "lg:col-span-5"
                                }`}
                            >
                                <div className={`relative overflow-hidden ${i === 0 ? "aspect-[16/10]" : "aspect-[16/9]"}`}>
                                    <img
                                        src={n.image}
                                        alt={n.title[lang] || n.title.de}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-6 md:p-7 flex-1 flex flex-col">
                                    <div className="flex items-center gap-3 text-xs font-sans uppercase tracking-widest text-caireen-ink/55">
                                        <span className={`px-2.5 py-1 rounded-full ${categoryColors[n.category] || "bg-caireen-ink/10 text-caireen-ink"}`}>
                                            {n.category}
                                        </span>
                                        <span>{formatDate(n.date, lang)}</span>
                                    </div>
                                    <h3 className={`mt-3 font-serif tracking-tight ${i === 0 ? "text-3xl md:text-4xl" : "text-2xl"}`}>
                                        {n.title[lang] || n.title.de}
                                    </h3>
                                    <p className="mt-2.5 font-sans text-sm text-caireen-ink/70 leading-relaxed flex-1">
                                        {n.excerpt[lang] || n.excerpt.de}
                                    </p>
                                    <div className="mt-5 inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-caireen-orange group-hover:text-caireen-red transition-colors">
                                        {t.newsPage.readMore}
                                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
